import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  Archive, 
  Eye, 
  EyeOff, 
  Users, 
  Calendar, 
  AlertTriangle,
  CheckCircle,
  X,
  Trash2,
  Settings
} from 'lucide-react';
import { authenticatedFetch } from '../utils/apiUtils';

const OrganizationManagement = ({ user, onClose }) => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archiveConfirm, setArchiveConfirm] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/organizations/my');
      if (!response.ok) {
        throw new Error('Failed to load organizations');
      }
      const data = await response.json();
      setOrganizations(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveOrganization = async (orgId) => {
    try {
      setProcessing(true);
      const response = await authenticatedFetch(`/api/organizations/${orgId}/archive`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error('Failed to archive organization');
      }

      // Update local state
      setOrganizations(prev => 
        prev.map(org => 
          org.id === orgId 
            ? { ...org, status: 'archived', archived_at: new Date().toISOString() }
            : org
        )
      );
      
      setArchiveConfirm(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleRestoreOrganization = async (orgId) => {
    try {
      setProcessing(true);
      const response = await authenticatedFetch(`/api/organizations/${orgId}/restore`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error('Failed to restore organization');
      }

      // Update local state
      setOrganizations(prev => 
        prev.map(org => 
          org.id === orgId 
            ? { ...org, status: 'active', archived_at: null }
            : org
        )
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const adminOrganizations = organizations.filter(org => org.role === 'admin');
  const activeOrgs = adminOrganizations.filter(org => org.status !== 'archived');
  const archivedOrgs = adminOrganizations.filter(org => org.status === 'archived');

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading organizations...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <Settings className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Organization Management</h2>
                <p className="text-blue-100 text-sm">Manage organizations where you're an administrator</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <p className="text-red-800 font-medium">Error</p>
              </div>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
          )}

          {adminOrganizations.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Organizations to Manage</h3>
              <p className="text-gray-600">You don't have administrator access to any organizations.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Active Organizations */}
              {activeOrgs.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-1 h-6 bg-green-500 rounded-full"></div>
                    <h3 className="text-lg font-semibold text-gray-900">Active Organizations</h3>
                    <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium">
                      {activeOrgs.length} active
                    </span>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    {activeOrgs.map(org => (
                      <OrganizationCard
                        key={org.id}
                        organization={org}
                        onArchive={() => setArchiveConfirm(org)}
                        formatDate={formatDate}
                        processing={processing}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Archived Organizations */}
              {archivedOrgs.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-1 h-6 bg-gray-400 rounded-full"></div>
                    <h3 className="text-lg font-semibold text-gray-900">Archived Organizations</h3>
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full font-medium">
                      {archivedOrgs.length} archived
                    </span>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    {archivedOrgs.map(org => (
                      <ArchivedOrganizationCard
                        key={org.id}
                        organization={org}
                        onRestore={() => handleRestoreOrganization(org.id)}
                        formatDate={formatDate}
                        processing={processing}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Archive Confirmation Modal */}
        {archiveConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Archive Organization</h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to archive <strong>{archiveConfirm.name}</strong>? 
                This will hide it from your active organizations list, but you can restore it later.
              </p>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setArchiveConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleArchiveOrganization(archiveConfirm.id)}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  disabled={processing}
                >
                  {processing ? 'Archiving...' : 'Archive'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const OrganizationCard = ({ organization, onArchive, formatDate, processing }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Building2 className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900">{organization.name}</h4>
            <p className="text-sm text-gray-600 capitalize">{organization.org_type || 'Organization'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium">
            Active
          </span>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4" />
          <span>Created {formatDate(organization.created_at)}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Users className="w-4 h-4" />
          <span>{organization.member_count || 0} members</span>
        </div>
        {organization.website_url && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="w-4 h-4 text-center">🌐</span>
            <a 
              href={organization.website_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline truncate"
            >
              {organization.website_url}
            </a>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          onClick={onArchive}
          disabled={processing}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          <Archive className="w-4 h-4" />
          Archive
        </button>
      </div>
    </div>
  );
};

const ArchivedOrganizationCard = ({ organization, onRestore, formatDate, processing }) => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 opacity-75">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-200 rounded-lg">
            <Building2 className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-700">{organization.name}</h4>
            <p className="text-sm text-gray-500 capitalize">{organization.org_type || 'Organization'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded-full font-medium">
            Archived
          </span>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Calendar className="w-4 h-4" />
          <span>Archived {formatDate(organization.archived_at)}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Users className="w-4 h-4" />
          <span>{organization.member_count || 0} members</span>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={onRestore}
          disabled={processing}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
        >
          <CheckCircle className="w-4 h-4" />
          Restore
        </button>
      </div>
    </div>
  );
};

export default OrganizationManagement; 